import { parkingFeesOn, type Role, type StaffMe } from './auth';

/**
 * The staff menu, as pure data and rules (no React): which pages each login is offered, in which section, and
 * where it lands after signing in. StaffLayout renders it; this file stays importable from unit tests.
 */
export function canAny(can: (perm: string) => boolean, perm: string | readonly string[]) {
  return typeof perm === 'string' ? can(perm) : perm.some((p) => can(p));
}

/**
 * Venue modules whose end-to-end spec is not green yet: their pages work by address but stay out of the menu.
 * A module leaves this list once its server tests and its end-to-end spec pass. Every module is live now
 * (parking, activities, settlements, facility, people, supply, cards and feedback).
 */
export const NOT_YET_LIVE = new Set<string>([]);

export interface NavItem {
  to: string;
  label: string;
  /** One permission, or a list of which any one is enough. */
  perm: string | readonly string[];
  section: string;
  /** Only the super admin and the accountant see it (venue-wide pages). */
  venueOnly?: boolean;
}

/** The venue menu: everyone except shop logins sees the entries their permissions allow, in this order. */
export const NAV: NavItem[] = [
  { to: '/admin/dashboard', label: 'Dashboard', perm: 'reports.read', section: 'Overview' },
  { to: '/admin/shops', label: 'All shops', perm: 'shops.overview', section: 'Overview' },
  { to: '/admin/shops/daily', label: 'Daily sales by shop', perm: 'shops.overview', section: 'Overview' },
  // The day-wise report: gate figures for every reports reader (the cashier sees their gate), the shops' figures for venue-wide logins.
  { to: '/admin/reports/daily', label: 'Daily report', perm: 'reports.read', section: 'Overview' },
  { to: '/admin/reports/visitors', label: 'Visitors', perm: 'reports.read', section: 'Overview' },
  // Every login may open the feed: the server sends each person only what they may see.
  { to: '/admin/live-activity', label: 'Live activity', perm: 'live.view', section: 'Overview' },
  // My business: the owner's own money view. The accountant sees the Venue P&L only (venue.finance.read).
  { to: '/admin/venue/pnl', label: 'Venue P&L', perm: 'venue.finance.read', section: 'My business' },
  { to: '/admin/venue/marketing', label: 'Marketing I buy', perm: 'venue.finance', section: 'My business' },
  { to: '/admin/venue/works', label: 'Renovation & works', perm: 'venue.finance', section: 'My business' },
  { to: '/admin/gate', label: 'Gate & vehicles inside', perm: 'visits.read', section: 'Parking' },
  { to: '/admin/visitors', label: 'Visitors & PAX', perm: 'visits.read', section: 'Parking' },
  { to: '/admin/live', label: 'Live park status', perm: 'reports.read', section: 'Parking' },
  { to: '/admin/collect', label: 'Collect payment', perm: 'payments.record', section: 'Parking' },
  { to: '/admin/visits', label: 'Visit history', perm: 'visits.read', section: 'Parking' },
  { to: '/admin/printer', label: 'Printer', perm: 'visits.write', section: 'Parking' },
  { to: '/admin/outlets', label: 'Shops & outlets', perm: 'outlets.manage', section: 'Shops' },
  { to: '/admin/games', label: 'Games & rides', perm: 'bookings.operate', section: 'Activities' },
  { to: '/admin/turf', label: 'Turf booking', perm: 'bookings.operate', section: 'Activities' },
  { to: '/admin/events', label: 'Events', perm: 'bookings.operate', section: 'Activities' },
  // The go-kart track: the desk (karts.operate; the track manager too), the report and the fleet for the manager.
  { to: '/admin/karts', label: 'Go-kart track', perm: ['karts.operate', 'karts.manage'], section: 'Activities' },
  { to: '/admin/karts/report', label: 'Kart report', perm: 'karts.manage', section: 'Activities' },
  { to: '/admin/settlements', label: 'Shop settlements', perm: 'settlements.read', section: 'Commerce' },
  { to: '/admin/dues', label: 'Dues from shops', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/ledger', label: 'Shop ledgers', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/agreements', label: 'Rentals & holding', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/closures', label: 'Closure register', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/advertisements', label: 'Ads I sell', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/cleaning-charges', label: 'Cleaning charges', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/generator', label: 'Generator & allocation', perm: 'settlements.manage', section: 'Commerce' },
  { to: '/admin/housekeeping', label: 'Housekeeping', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/quality', label: 'Quality control', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/pest-control', label: 'Pest control', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/maintenance', label: 'Maintenance', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/assets', label: 'Assets', perm: 'facility.manage', section: 'Facility' },
  { to: '/admin/other-services', label: 'Other services', perm: ['facility.operate', 'facility.manage'], section: 'Facility' },
  { to: '/admin/employees', label: 'Employees', perm: ['people.operate', 'people.manage'], section: 'People' },
  { to: '/admin/attendance', label: 'Attendance', perm: 'people.operate', section: 'People' },
  { to: '/admin/payroll', label: 'Payroll', perm: 'payroll.manage', section: 'People' },
  { to: '/admin/hr-reminders', label: 'HR payment reminders', perm: ['people.manage', 'payroll.manage'], section: 'People' },
  { to: '/admin/accommodation', label: 'Accommodation', perm: 'people.operate', section: 'People' },
  { to: '/admin/materials', label: 'Materials & stock', perm: ['supply.operate', 'supply.manage'], section: 'Supply chain' },
  { to: '/admin/material-requests', label: 'Material requests', perm: ['supply.operate', 'supply.manage'], section: 'Supply chain' },
  { to: '/admin/procurement', label: 'Procurement', perm: 'supply.manage', section: 'Supply chain' },
  { to: '/admin/supplier-bills', label: 'Supplier bills', perm: 'supply.manage', section: 'Supply chain' },
  { to: '/admin/payments', label: 'Payments', perm: 'payments.read', section: 'Money' },
  { to: '/admin/closing', label: 'Daily closing', perm: 'closings.read', section: 'Money' },
  { to: '/admin/finance', label: 'Income & expenses', perm: 'finance.read', section: 'Money' },
  // Old records (shop sales, rent received, expenses) from Excel: here too, so the accountant (Money only) finds it.
  { to: '/admin/imports', label: 'Import old data', perm: 'imports.manage', section: 'Money' },
  { to: '/admin/reports', label: 'Reports', perm: 'reports.read', section: 'Money' },
  // Money loaded into wallets, spent from them by shop, the float held and what is owed to independent shops (head office).
  { to: '/admin/reports/wallet', label: 'Wallet & recharges', perm: 'wallet.manage', section: 'Money' },
  // The reception: the customer pays here (cash, UPI, card) and the money goes into their wallet to spend at the counters.
  { to: '/admin/reception', label: 'Recharge wallet', perm: 'wallet.recharge', section: 'Reception' },
  { to: '/admin/customers', label: 'Customers', perm: 'customers.lookup', section: 'Customers' },
  { to: '/admin/cards', label: 'Cards & recharge', perm: 'cards.operate', section: 'Customers' },
  { to: '/admin/coupons', label: 'Redeem coupon', perm: 'coupons.redeem', section: 'Customers' },
  { to: '/admin/campaigns', label: 'Campaigns', perm: 'campaigns.read', section: 'Customers' },
  { to: '/admin/notifications', label: 'Notifications', perm: 'notifications.manage', section: 'Customers' },
  { to: '/admin/feedback', label: 'Feedback', perm: 'feedback.read', section: 'Customers' },
  { to: '/admin/users', label: 'Staff users', perm: 'users.manage', section: 'Admin' },
  { to: '/admin/settings', label: 'Settings', perm: 'settings.manage', section: 'Admin' },
  { to: '/admin/pricing', label: 'Pricing rules', perm: 'pricing.read', section: 'Admin' },
  { to: '/admin/cameras', label: 'Cameras & plates', perm: 'cameras.manage', section: 'Admin' },
  { to: '/admin/areas', label: 'Vehicle areas (RTO)', perm: 'settings.manage', section: 'Admin' },
  { to: '/admin/imports', label: 'Data import', perm: 'imports.manage', section: 'Admin' },
  { to: '/admin/terms', label: 'Terms & conditions', perm: 'terms.manage', section: 'Admin' },
  { to: '/admin/audit', label: 'Audit log', perm: 'audit.read', section: 'Admin' },
];

/** A shop's own menu, for its admin and counter logins (one "My shop" section). */
export const SHOP_NAV: NavItem[] = [
  { to: '/admin/shop-home', label: 'Home', perm: 'shop.home', section: 'My shop' },
  { to: '/admin/pos', label: 'Billing', perm: 'pos.sell', section: 'My shop' },
  { to: '/admin/live-activity', label: 'Live activity', perm: 'live.view', section: 'My shop' },
  { to: '/admin/bills', label: 'Bills', perm: 'sales.read', section: 'My shop' },
  { to: '/admin/products', label: 'Products & stock', perm: 'products.read', section: 'My shop' },
  { to: '/admin/finance', label: 'Expenses', perm: 'finance.read', section: 'My shop' },
  { to: '/admin/closing', label: 'Close the day', perm: 'closings.read', section: 'My shop' },
  { to: '/admin/shop-pnl', label: 'Profit & loss', perm: 'shop.reports', section: 'My shop' },
  // The shop's own day-wise sales: by day, products sold, by menu and low sellers (the server scopes it to the shop).
  { to: '/admin/shops/daily', label: 'Daily sales', perm: 'shop.reports', section: 'My shop' },
  { to: '/admin/shop-staff', label: 'Staff logins', perm: 'shop.users', section: 'My shop' },
  { to: '/admin/shop-settings', label: 'Receipt & shop details', perm: 'shop.settings', section: 'My shop' },
  { to: '/admin/printer', label: 'Printer', perm: 'pos.sell', section: 'My shop' },
  { to: '/admin/settlements', label: 'Venue statements', perm: 'settlements.read', section: 'My shop' },
  // The shop's rent terms, balance and ledger (shop admins only: counters never have settlements.read).
  { to: '/admin/rent', label: 'Rent & venue charges', perm: 'settlements.read', section: 'My shop' },
  // The shop's own guest reviews (the page is scoped to the shop by the server).
  { to: '/admin/feedback', label: 'Feedback', perm: 'feedback.read', section: 'My shop' },
];

/** What the super admin and the accountant see inside one shop ("All shops" → Open shop). */
const DRILL_NAV: NavItem[] = [
  { to: '/admin/shop-home', label: 'Home', perm: 'shop.home', section: 'Shop' },
  { to: '/admin/bills', label: 'Bills', perm: 'sales.read', section: 'Shop' },
  { to: '/admin/products', label: 'Products & stock', perm: 'products.read', section: 'Shop' },
  { to: '/admin/shop-pnl', label: 'Profit & loss', perm: 'shop.reports', section: 'Shop' },
  { to: '/admin/shop-staff', label: 'Staff logins', perm: 'shop.users', section: 'Shop' },
  { to: '/admin/shop-settings', label: 'Receipt & shop details', perm: 'shop.settings', section: 'Shop' },
  { to: '/admin/closing', label: 'Daily closing', perm: 'closings.read', section: 'Shop' },
  { to: '/admin/finance', label: 'Expenses', perm: 'finance.read', section: 'Shop' },
];
/** Added to the shop's menu while the super admin works the counter of a venue-run outlet. */
const COUNTER_NAV: NavItem[] = [
  { to: '/admin/pos', label: 'Billing', perm: 'pos.sell', section: 'Shop' },
  { to: '/admin/printer', label: 'Printer', perm: 'pos.sell', section: 'Shop' },
];

/**
 * The accountant's menu: overview, money and the money-related venue pages (statements, payroll, and the supply
 * chain, where the accountant records, approves and pays supplier bills and raises purchase orders).
 */
export const ACCOUNTANT_SECTIONS = new Set(['Overview', 'My business', 'Money', 'Commerce', 'People', 'Supply chain']);

const SHOP_ROLES: Role[] = ['shop_admin', 'shop_staff'];

/**
 * Where a venue staff login starts its day, from the job its login name says (the seed names the housekeeping
 * and maintenance logins after their job; the owner's own logins usually follow suit). Housekeeping and
 * maintenance share the same grant, so the grant alone cannot tell them apart.
 */
export function venueStaffPreferred(login: string): string[] {
  const l = login.toLowerCase();
  if (/maint|repair|electric|plumb/.test(l)) return ['/admin/maintenance'];
  if (/house|clean|hk\b/.test(l)) return ['/admin/housekeeping'];
  if (/\bhr\b|payroll|people|attend/.test(l)) return ['/admin/attendance', '/admin/employees'];
  if (/store|supply|material|purchase/.test(l)) return ['/admin/materials'];
  if (/kart|track/.test(l)) return ['/admin/karts'];
  if (/turf/.test(l)) return ['/admin/turf'];
  if (/desk|arena|turf|game|event|booking/.test(l)) return ['/admin/games', '/admin/turf', '/admin/events'];
  return [];
}

export interface Menu {
  sections: { title: string; items: NavItem[] }[];
}

function group(items: NavItem[]): Menu {
  const titles = [...new Set(items.map((i) => i.section))];
  return { sections: titles.map((title) => ({ title, items: items.filter((i) => i.section === title) })) };
}

/**
 * The cashier works at the reception (the client: "first the customer pays the amount to the reception"): the pages
 * of that job sit together under one "Reception" heading, in the order of the day's work — load a wallet, find a
 * customer, the gate and the visit history. Other logins keep these pages in their usual sections.
 */
export const RECEPTION_PAGES = ['/admin/reception', '/admin/customers', '/admin/gate', '/admin/visits'];

function receptionMenu(items: NavItem[]): NavItem[] {
  const reception = RECEPTION_PAGES.map((to) => items.find((i) => i.to === to))
    .filter((i): i is NavItem => !!i)
    .map((i) => ({ ...i, section: 'Reception' }));
  const rest = items.filter((i) => !RECEPTION_PAGES.includes(i.to));
  const at = rest.findIndex((i) => i.section !== 'Overview');
  const cut = at < 0 ? rest.length : at;
  return [...rest.slice(0, cut), ...reception, ...rest.slice(cut)];
}

/** Pages about parking money: offered only while the owner charges for parking (setting parking.feesEnabled). */
const PARKING_FEE_PAGES = new Set(['/admin/collect', '/admin/pricing']);
/** With parking free, the reception cashier's Parking section is the gate board and the visit history only. */
const CASHIER_FREE_PARKING_PAGES = new Set(['/admin/gate', '/admin/visits']);

/** The menu a login sees right now. Each entry is also guarded by its route, so this only decides what is offered. */
export function menuFor(
  me: StaffMe,
  can: (perm: string) => boolean,
  opts: { viewingShop?: boolean; workAtCounter?: boolean; simulation?: boolean; venueRunShop?: boolean } = {},
): Menu {
  const role = me.user.role;
  const fees = parkingFeesOn(me);
  const parkingPage = (i: NavItem) =>
    fees || (!PARKING_FEE_PAGES.has(i.to) && (role !== 'cashier' || i.section !== 'Parking' || CASHIER_FREE_PARKING_PAGES.has(i.to)));
  const allowed = (i: NavItem) => canAny(can, i.perm) && !NOT_YET_LIVE.has(i.to) && parkingPage(i);
  // A shop run by the venue itself pays no rent or commission, so it never gets venue statements: no empty page.
  if (SHOP_ROLES.includes(role)) return group(SHOP_NAV.filter((i) => allowed(i) && !(opts.venueRunShop && (i.to === '/admin/settlements' || i.to === '/admin/rent'))));
  if (opts.viewingShop) return group([...DRILL_NAV, ...(opts.workAtCounter ? COUNTER_NAV : [])].filter(allowed));
  const venueWide = role === 'owner' || role === 'accountant';
  let items = NAV.filter((i) => allowed(i) && (!i.venueOnly || venueWide));
  if (role === 'accountant') items = items.filter((i) => ACCOUNTANT_SECTIONS.has(i.section));
  if (role === 'cashier') items = receptionMenu(items);
  if (opts.simulation && can('simulation.run')) {
    const at = items.findIndex((i) => i.section !== 'Overview');
    items.splice(at < 0 ? items.length : at, 0, { to: '/admin/simulate', label: 'Simulate operations', perm: 'simulation.run', section: 'Overview' });
  }
  return group(items);
}

/**
 * The first screen after signing in: the most useful page for the role, else the first menu entry the login
 * may open, else My account. Never a page the login cannot open (that would bounce back here for ever).
 */
export function firstPage(me: StaffMe, can: (perm: string) => boolean): string {
  const role = me.user.role;
  const menu = menuFor(me, can).sections.flatMap((s) => s.items);
  const offered = (to: string) => menu.some((i) => i.to === to);
  const preferred: Partial<Record<Role, string[]>> = {
    security: ['/admin/gate'],
    // Collect payment exists only while parking is charged for; otherwise the reception cashier starts at the gate board.
    // The cashier's day starts at the reception (wallet recharges); then fee collection where parking is charged, else the gate.
    cashier: ['/admin/reception', '/admin/collect', '/admin/gate'],
    shop_admin: ['/admin/shop-home', '/admin/shop-pnl'],
    shop_staff: ['/admin/pos', '/admin/shop-home'],
    owner: ['/admin/dashboard'],
    accountant: ['/admin/dashboard'],
    venue_staff: venueStaffPreferred(me.user.login),
  };
  // A venue login whose only module is not in the menu yet still lands on that module's page.
  const hidden = SHOP_ROLES.includes(role) ? undefined : NAV.find((i) => canAny(can, i.perm) && (!i.venueOnly || role === 'owner' || role === 'accountant'));
  // Every login has the live feed; it is a page to look in on, not where the day's work starts.
  const work = menu.find((i) => i.to !== '/admin/live-activity');
  return (preferred[role] ?? []).find(offered) ?? work?.to ?? hidden?.to ?? menu[0]?.to ?? '/admin/account';
}
